import { createAdminApi, type AdminApi } from './api/admin';
import { createTenantApi, type TenantApi } from './api/tenant';
import { createPlatformApi, type PlatformApi } from './api/platform';
import { createAppVersionApi, type AppVersionApi } from './api/appVersion';
import { createOpsExtrasApi, type OpsExtrasApi } from './api/opsExtras';
import { createAuditApi, type AuditApi } from './api/audit';
import { createOrgApi, type OrgApi } from './api/org';
import { createOrgGroupApi, type OrgGroupApi } from './api/orgGroup';
import { createOrgStructureApi, type OrgStructureApi } from './api/orgStructure';
import { createAnalyticsApi, type AnalyticsApi } from './api/analytics';
import { createGovApi, type GovApi } from './api/gov';
import { createApplyApi, type ApplyApi } from './api/apply';
import { createApprovalApi, type ApprovalApi } from './api/approval';
import { createApprovalFlowApi, type ApprovalFlowApi } from './api/approvalFlow';
import { createBillingApi, type BillingApi } from './api/billing';
import { createAuthApi, type AuthApi } from './api/auth';
import { createCatalogApi, type CatalogApi } from './api/catalog';
import { createProcurementApi, type ProcurementApi } from './api/procurement';
import { createDeliveryApi, type DeliveryApi } from './api/delivery';
import { createOrderCenterApi, type OrderCenterApi } from './api/orderCenter';
import { createFileApi, type FileApi } from './api/file';
import { createInboundApi, type InboundApi } from './api/inbound';
import { createInventoryOpsApi, type InventoryOpsApi } from './api/inventoryOps';
import { createMaterialApi, type MaterialApi } from './api/material';
import { createMessageApi, type MessageApi } from './api/message';
import { createSatisfactionApi, type SatisfactionApi } from './api/satisfaction';
import { createWorkorderApi, type WorkorderApi } from './api/workorder';
import { createOutboundApi, type OutboundApi } from './api/outbound';
import { createPriceChangeApi, type PriceChangeApi } from './api/priceChange';
import { createReplenishApi, type ReplenishApi } from './api/replenish';
import { createStockRuleApi, type StockRuleApi } from './api/stockRule';
import { createReturnApi, type ReturnApi } from './api/return';
import { createShortageApi, type ShortageApi } from './api/shortage';
import { createStockAlertApi, type StockAlertApi } from './api/stockAlert';
import { createStockApi, type StockApi } from './api/stock';
import { createSupplyApi, type SupplyApi } from './api/supply';
import { createTrackApi, type TrackApi } from './api/track';
import { createRfidApi, type RfidApi } from './api/rfid';
import { createBindApi, type BindApi } from './api/bind';
import { createScanApi, type ScanApi } from './api/scan';
import { createFavoriteApi, type FavoriteApi } from './api/favorite';
import { createAiChatApi, type AiChatApi } from './api/aiChat';
import { createAiInsightsApi, type AiInsightsApi } from './api/aiInsights';
import { createWxSubscribeApi, type WxSubscribeApi } from './api/wxSubscribe';
import { createDingTalkApi, type DingTalkApi } from './api/dingtalk';
import { createAlipayApi, type AlipayApi } from './api/alipay';
import { createGeoApi, type GeoApi } from './api/geo';
import { createConsumptionApi, type ConsumptionApi } from './api/consumption';
import { createStatsApi, type StatsApi } from './api/stats';
import { createHttpClient, type HttpClient, type HttpClientOptions } from './client';

export type {
  HttpAdapter,
  HttpCacheOptions,
  HttpClient,
  HttpClientOptions,
  HttpMethod,
  HttpRequest,
  HttpResponse,
  QueryValue,
  RequestOptions
} from './client';
export { createHttpClient, toQuery } from './client';
export {
  CachePolicy,
  defaultCacheRules,
  matchCacheRule,
  type CacheRule
} from './cache';

export { createFetchAdapter, type FetchAdapterOptions } from './adapters/fetch';
export { RequestTimeoutError, isTimeoutError } from './errors';

export type { ApplyApi, ApplyListQuery, CancelApplyResult } from './api/apply';
export { createApplyApi } from './api/apply';
export type { ApprovalApi } from './api/approval';
export { createApprovalApi } from './api/approval';
export type { ApprovalFlowApi } from './api/approvalFlow';
export { createApprovalFlowApi } from './api/approvalFlow';
export type {
  ApprovalBizTypeRow,
  ApprovalDelegateRow,
  ApprovalFlowAuthRow,
  ApprovalFlowAuthSaveRequest,
  ApprovalFlowDetail,
  ApprovalFlowRow,
  ApprovalFlowSaveRequest,
  ApprovalFlowStatus,
  ApprovalNodeInput,
  ApprovalNodeRow,
  ApprovalNodeUserInput,
  ApprovalNodeUserRow,
  ApprovalRouteInput,
  ApprovalRouteRow,
  ApprovalVariableRow
} from './types/approvalFlow';
export type {
  BillingApi,
  BudgetBalanceDeptRow,
  BudgetBalanceQuery,
  BudgetBalanceResult,
  BudgetPlanApproveRequest,
  BudgetPlanDetail,
  BudgetPlanItemInput,
  BudgetPlanItemRow,
  BudgetPlanQuery,
  BudgetPlanRow,
  BudgetPlanSaveRequest,
  BudgetPlanStatus,
  BudgetPlanType,
  BudgetUsageQuery,
  BudgetUsageRow,
  InvoiceRow,
  PaymentResult,
  PaymentRow,
  SettleDetail,
  SettleGenerateRequest,
  SettleGenerateResult,
  SettleInvoiceRequest,
  SettleItemRow,
  SettleListQuery,
  SettlePaymentRequest,
  SettleRow,
  ServiceFeeQuery,
  ServiceFeeRow,
  ServiceFeeSaveRequest
} from './api/billing';
export { createBillingApi } from './api/billing';
export type { AuthApi } from './api/auth';
export { createAuthApi } from './api/auth';
export type { DeliveryApi } from './api/delivery';
export { createDeliveryApi } from './api/delivery';
export type { TrackApi } from './api/track';
export { createTrackApi } from './api/track';
export type { RfidApi } from './api/rfid';
export { createRfidApi } from './api/rfid';
export type {
  RfidLogRow,
  RfidQuery,
  RfidReadItem,
  RfidReadMode,
  RfidReadRequest,
  RfidReadResult
} from './types/rfid';
export type {
  BindApi,
  BindQrCreateRequest,
  BindQrCreateResult,
  BindQrType,
  BindQrVerifyResult,
  WxBindRequest,
  WxBindResult,
  WxBoundResult,
  WxLoginRequest,
  WxLoginResult,
  WxBindByPhoneRequest,
  WxBindByPhoneResult,
  BindApplyRow,
  BindApplyAuditRequest,
  BindApplyAuditResult,
  BindUserSearchRow,
  MiniCodeResult
} from './api/bind';
export { createBindApi } from './api/bind';
export type { ScanAction, ScanApi, ScanResolveRequest, ScanResolveResult } from './api/scan';
export { createScanApi } from './api/scan';
export type { FavoriteApi, FavoriteRow, FavoriteSaveRequest } from './api/favorite';
export { createFavoriteApi } from './api/favorite';
export type { AiChatAnswer, AiChatApi, AiChatHistoryRow, AiChatRequest } from './api/aiChat';
export { createAiChatApi } from './api/aiChat';
export type {
  AiInsightsApi,
  EvidenceActionRow,
  EvidenceAlertRow,
  EvidenceBudgetRow,
  EvidenceCostRow,
  EvidenceDeliveryRow,
  EvidenceItemRow,
  EvidenceSettleItemRow,
  EvidenceTaskRow,
  EvidenceTraceQuery,
  EvidenceTraceResult,
  EvidenceTrackRow,
  ForecastPeriodType,
  ForecastRow,
  ForecastRunRequest,
  RecommendConfirmRequest,
  RecommendDecision,
  RecommendRow
} from './api/aiInsights';
export { createAiInsightsApi } from './api/aiInsights';
export type { FileApi } from './api/file';
export { createFileApi } from './api/file';
export type { InboundApi } from './api/inbound';
export { createInboundApi } from './api/inbound';
export type {
  InventoryOpsApi,
  StocktakeCountRequest,
  StocktakeCountResult,
  StocktakeCreateRequest,
  StocktakeCreateResult,
  StocktakeDetail,
  StocktakeItemRow,
  StocktakeListQuery,
  StocktakeRow,
  TransferDetail,
  TransferItemRow,
  TransferListQuery,
  TransferRow
} from './api/inventoryOps';
export { createInventoryOpsApi } from './api/inventoryOps';
export type { MaterialApi } from './api/material';
export { createMaterialApi } from './api/material';
export type {
  SatisfactionApi,
  SatisfactionCreateRequest,
  SatisfactionCreateResult,
  SatisfactionEvalType,
  SatisfactionListQuery,
  SatisfactionListResult,
  SatisfactionReplyResult,
  SatisfactionRow,
  SatisfactionStats
} from './api/satisfaction';
export { createSatisfactionApi } from './api/satisfaction';
export type {
  WorkorderAction,
  WorkorderApi,
  WorkorderAssignRequest,
  WorkorderCreateRequest,
  WorkorderCreateResult,
  WorkorderHandleRequest,
  WorkorderHandleResult,
  WorkorderListQuery,
  WorkorderRow,
  WorkorderStatus,
  WorkorderType
} from './api/workorder';
export { createWorkorderApi } from './api/workorder';
export type {
  MessageApi,
  ServiceMessageListQuery,
  ServiceMessageRow,
  ServiceMessageSendRequest,
  ServiceMessageSendResult,
  ServiceMessageStats,
  ServiceSenderType,
  ServiceSessionType
} from './api/message';
export { createMessageApi } from './api/message';
export type {
  PriceChangeAction,
  PriceChangeApi,
  PriceChangeCreateRequest,
  PriceChangeDecideRequest,
  PriceChangeDecisionResult,
  PriceChangeDetail,
  PriceChangeInstance,
  PriceChangeListQuery,
  PriceChangeLog,
  PriceChangeRow,
  PriceChangeStatus,
  PriceChangeTask
} from './api/priceChange';
export { createPriceChangeApi } from './api/priceChange';
export type { OutboundApi } from './api/outbound';
export { createOutboundApi } from './api/outbound';
export type { ReturnApi } from './api/return';
export { createReturnApi } from './api/return';
export type {
  SupplierSupplyDetail,
  SupplierSupplyOrder,
  SupplyApi,
  SupplyDetail,
  SupplyItemRow,
  SupplyListQuery,
  SupplyOutResult,
  SupplyRow
} from './api/supply';
export { createSupplyApi } from './api/supply';
export type { CatalogApi } from './api/catalog';
export { createCatalogApi } from './api/catalog';
export type { ProcurementApi } from './api/procurement';
export { createProcurementApi } from './api/procurement';
export type { AnalyticsApi } from './api/analytics';
export { createAnalyticsApi } from './api/analytics';
export type { GovApi } from './api/gov';
export { createGovApi } from './api/gov';
export type { AdminApi } from './api/admin';
export { createAdminApi } from './api/admin';
export type { TenantApi } from './api/tenant';
export { createTenantApi } from './api/tenant';
export type { PlatformApi, PlatformDeleteResult, PlatformSaveResult } from './api/platform';
export { createPlatformApi } from './api/platform';
export type { AppVersionApi } from './api/appVersion';
export { createAppVersionApi } from './api/appVersion';
export type {
  AppDeleteResult,
  AppPlatform,
  AppReleasePatchRequest,
  AppReleaseRecordRequest,
  AppReleaseRecordResult,
  AppReleaseRow,
  AppSetCurrentResult,
  AppVersionRow,
  AppVersionSaveRequest,
  AppVersionSaveResult,
  CheckUpdateRequest,
  CheckUpdateResult,
  QrAttachRequest
} from './types/appVersion';
export type {
  MessageChannel,
  MessageTplRow,
  MessageTplSaveRequest,
  MetricDefRow,
  MetricDefSaveRequest,
  MetricGroup,
  ParamValueType,
  PlatformParamRow,
  PlatformParamSaveRequest,
  TplKind,
  TplRow,
  TplSaveRequest,
  TplStatus
} from './types/platform';
export type {
  ChinaRegionNode,
  OpsCenterItem,
  PlatformOverviewResult,
  RegionDeleteResult,
  RegionImportItem,
  RegionImportRequest,
  RegionImportResult,
  RegionOptionRow,
  RegionQuery,
  RegionRow,
  RegionSaveRequest,
  RegionSaveResult,
  ScreenDeviceApproveRequest,
  ScreenDevicePendingRow,
  ScreenDeviceRow,
  ScreenDeviceStatus,
  ScreenDeviceTargets,
  TenantAdminItem,
  TenantAdminRow,
  TenantAdminSaveRequest,
  TenantAdminSaveResult,
  TenantCenterRow,
  TenantCreateRequest,
  TenantDeployMode,
  TenantEnterResult,
  TenantLifecycleResult,
  TenantMetricRow,
  TenantOrgRow,
  TenantOrgsResult,
  TenantQuery,
  TenantRenewRequest,
  TenantRenewResult,
  TenantRow,
  TenantSaveResult,
  TenantStatus,
  TenantTemplateApplyResult,
  TenantTemplateSelection,
  TenantUpdateRequest,
  OpsCenterSaveRequest,
  OpsCenterSaveResult
} from './types/tenant';
export type { AuditApi } from './api/audit';
export { createAuditApi } from './api/audit';
export type { AuditLogQuery, AuditLogRow, AuditSummaryResult } from './types/audit';
export type { OrgApi } from './api/org';
export { createOrgApi } from './api/org';
export type { OrgGroupApi } from './api/orgGroup';
export { createOrgGroupApi } from './api/orgGroup';
export type {
  OrgGroupDeleteResult,
  OrgGroupQuery,
  OrgGroupRow,
  OrgGroupSaveRequest,
  OrgGroupSaveResult,
  OrgGroupType
} from './types/orgGroup';
export type { OrgStructureApi } from './api/orgStructure';
export { createOrgStructureApi } from './api/orgStructure';
export type {
  BuildingQuery,
  BuildingRow,
  BuildingSaveRequest,
  DeptQuery,
  DeptRow,
  DeptSaveRequest,
  DeptType,
  FloorQuery,
  FloorRow,
  FloorSaveRequest,
  LocationQuery,
  LocationRow,
  LocationSaveRequest,
  LocationZoneType,
  OrgStatusFlag,
  SpaceStatus,
  OrgStructureDeleteResult,
  OrgStructureSaveResult,
  SpaceNodeType,
  WarehouseKind,
  WarehouseNature,
  WarehouseQuery,
  WarehouseRow,
  WarehouseSaveRequest
} from './types/orgStructure';
export type {
  OrgDeleteResult,
  OrgInstRole,
  OrgInstType,
  OrgOptionQuery,
  OrgOptionRow,
  OrgQuery,
  OrgRow,
  OrgSaveRequest,
  OrgSaveResult,
  OrgStatus,
  OrgSuperviseLevel
} from './types/org';

export type {
  AdminPermRow,
  AdminRoleCreateRequest,
  AdminRoleRow,
  AdminUserCreateRequest,
  AdminUserCreateResult,
  AdminUserPreflightQuery,
  AdminUserPreflightResult,
  AdminUserQuery,
  AdminUserRow,
  AdminUserUpdateRequest
} from './types/admin';

export type {
  CategoryRow,
  CategoryListQuery,
  CatalogListQuery,
  ContractQuery,
  ContractRow,
  ContractSaveRequest,
  MaterialPriceQuery,
  MaterialPriceRow,
  SupplierAdmissionRequest,
  SupplierDetail,
  SupplierListQuery,
  SupplierMaterialQuery,
  SupplierMaterialRow,
  SupplierMaterialSaveRequest,
  SupplierQualRow,
  SupplierQualSaveRequest,
  SupplierRow,
  SupplierSaveRequest,
  UnitRow
} from './types/catalog';

export type {
  PurchaseCreateRequest,
  PurchaseDetail,
  PurchaseItemInput,
  PurchaseItemRow,
  PurchaseListQuery,
  PurchaseReceiveItem,
  PurchaseReceiveRequest,
  PurchaseReceiveResult,
  PurchaseRow,
  RequireCreateRequest,
  RequireDetail,
  RequireItemInput,
  RequireItemRow,
  RequireListQuery,
  RequireRow
} from './types/procurement';

export type {
  AnalyticsQuery,
  CategoryMetricRow,
  GovInstQuery,
  GovInstRow,
  GovPeriodType,
  GovSummaryRow,
  MaterialMetricRow,
  OrgMetricRow,
  OverviewRow
} from './types/analytics';

export type {
  GovCategoryMetricRow,
  GovDeptRow,
  GovDrillLevel,
  GovDrillQuery,
  GovDrillResult,
  GovGroupResult,
  GovPerformanceItem,
  GovPerformanceResult,
  GovQuery,
  GovRankItem,
  GovRankResult,
  GovRefreshState,
  GovReportResult,
  GovReportSnapshot,
  GovSkuMetricRow,
  GovStructureItem,
  GovTopicResult
} from './types/gov';

export type {
  ConfirmResult,
  InboundDetail,
  InboundItemRow,
  InboundListQuery,
  InboundPutawayItem,
  InboundPutawayRequest,
  InboundPutawayResult,
  InboundRow,
  OutboundDetail,
  OutboundItemRow,
  OutboundListQuery,
  OutboundPickItem,
  OutboundPickRequest,
  OutboundPickResult,
  OutboundRow
} from './types/inventoryDoc';

export type {
  BudgetQuotaHeader,
  BudgetQuotaItem,
  BudgetQuotaResult,
  DictItemRow,
  DictItemSaveRequest,
  DictRow,
  DictSaveRequest,
  InquiryCreateRequest,
  InquiryDetail,
  InquiryItemInput,
  InquiryItemRow,
  InquiryRow,
  InquiryStatus,
  InterfaceLogQuery,
  InterfaceLogRow,
  QuoteCreateRequest,
  QuoteItemInput,
  QuoteItemRow,
  QuoteRow,
  SplitOrderRequest,
  SplitOrderResult,
  WaveAssignRequest,
  WaveOrderRow,
  WaveRow
} from './types/opsExtras';

export type { StockApi } from './api/stock';
export { createStockApi } from './api/stock';
export type { StockAlertApi } from './api/stockAlert';
export { createStockAlertApi } from './api/stockAlert';
export type { ShortageApi } from './api/shortage';
export { createShortageApi } from './api/shortage';
export type { ReplenishApi } from './api/replenish';
export { createReplenishApi } from './api/replenish';
export type {
  ReplenishAdoptRequest,
  ReplenishAdoptResult,
  ReplenishFlowType,
  ReplenishGenerateResult,
  ReplenishIgnoreRequest,
  ReplenishIgnoreResult,
  ReplenishQuery,
  ReplenishRow,
  ReplenishStatus
} from './types/replenish';
export type { StockRuleApi } from './api/stockRule';
export { createStockRuleApi } from './api/stockRule';
export type {
  StockRuleAlertLevel,
  StockRuleCalcType,
  StockRuleDeleteResult,
  StockRuleQuery,
  StockRuleRow,
  StockRuleSaveRequest,
  StockRuleSaveResult,
  StockRuleScopeType
} from './types/stockRule';
export type {
  CreateShortageRequest,
  HandleShortageRequest,
  ShortageAuditStatus,
  ShortageHandleResult,
  ShortageHandleResultRow,
  ShortageQuery,
  ShortageReason,
  ShortageRow,
  ShortageUrgency
} from './types/shortage';
export type {
  StockAlertCloseAction,
  StockAlertCloseResult,
  StockAlertProcessAction,
  StockAlertProcessResult,
  StockAlertQuery,
  StockAlertRow,
  StockAlertScanResult,
  StockAlertSummary
} from './types/stockAlert';

export type {
  ReturnActionResult,
  ReturnCheckRequest,
  ReturnCheckResult,
  ReturnConfirmResult,
  ReturnCreateItem,
  ReturnCreateRequest,
  ReturnDetail,
  ReturnItemRow,
  ReturnListQuery,
  ReturnPickupRequest,
  ReturnPickupResult,
  ReturnReasonType,
  ReturnRejectRequest,
  ReturnRow,
  ReturnStatus,
  ReturnType
} from './types/return';

export type { MaterialListQuery, MaterialRow } from './types/material';
export type { LedgerListQuery, LedgerRow, StockListQuery, StockRow } from './types/stock';

export type {
  ApprovalCapabilities,
  ApprovalTaskRow,
  ApprovalUserPick,
  ApprovalTrace,
  ApprovalTraceInstance,
  ApprovalTraceLog,
  ApprovalTraceTask
} from './types/approval';
export type {
  CreateDeliveryRequest,
  DeliveryDetail,
  DeliveryItemRow,
  DeliveryListQuery,
  DeliveryRow,
  DeliveryStatus,
  SignAttachInput,
  SignAttachRow,
  SignDeliveryRequest,
  SignDetail,
  SignItemInput,
  SignListQuery,
  SignResult,
  SignRow
} from './types/delivery';

export type {
  TrackQuery,
  TrackReportRequest,
  TrackReportResult,
  TrackRow,
  TrackType
} from './types/track';

export type {
  OrderAcceptResult,
  OrderCenterDetail,
  OrderCenterItem,
  OrderCenterQuery,
  OrderCenterRow,
  OrderRejectResult,
  SupplyTriggerType
} from './types/orderCenter';

export type {
  ApiCodeValue,
  ApiEnvelope,
  ApiErrorInit,
  PageQuery,
  PageResult,
  ResourceErrorBody
} from './types/result';
export { ApiCode, ApiError, httpStatusToCode } from './types/result';

export type {
  AllocateLine,
  AllocateResult,
  AllocateResultType,
  ApplyBizStatus,
  ApplyDetail,
  ApplyItemInput,
  ApplyItemRow,
  ApplyRow,
  ApproveAction,
  ApproveRequest,
  CreateApplyRequest,
  ApproveResult,
  SubmitApplyRequest,
  SubmitApplyResult,
  UrgentLevel
} from './types/apply';

export type {
  AttachmentDto,
  BindFileResult,
  DeleteFileResult,
  FileKind,
  SignedUrlDto,
  UploadFileOptions
} from './types/file';

export type {
  AccountQrActiveResult,
  AccountQrIdentity,
  AccountQrIssueResult,
  ChangePasswordRequest,
  HqAuthClaims,
  LoginRequest,
  LoginResult,
  OrgConfigItem,
  OrgConfigResult,
  OrgConfigSaveResult,
  QrShareCreateResult,
  ScreenDeviceClaimResult,
  ScreenDeviceInfo,
  ScreenDeviceRequestInput,
  ScreenDeviceRequestResult,
  ScreenTokenRequest,
  ScreenTokenResult,
  SwitchOrgResult,
  SwitchableOrgResult,
  SwitchableOrgRow,
  TokenResult,
  UpdateMyProfileRequest,
  UserProfile
} from './types/auth';

export type {
  WxSubscribeApi,
  WxSubscribeKey,
  WxSubscribeSendRequest,
  WxSubscribeSendResult,
  WxTemplateItem,
  WxTemplateListResult
} from './api/wxSubscribe';

export type {
  DingTalkApi,
  DingTalkBindResult,
  DingTalkLoginRequest,
  DingTalkLoginResult
} from './api/dingtalk';

export type {
  AlipayApi,
  AlipayBindResult,
  AlipayLoginRequest,
  AlipayLoginResult
} from './api/alipay';

export type { AmapWebConfig, GeoBackfillRow, GeocodeQuery, GeoPoint } from './types/geo';
export type { GeoApi } from './api/geo';
export { createGeoApi } from './api/geo';
export type {
  ConsumptionApi,
  CostConsumeItemInput,
  CostConsumeRequest,
  CostConsumeResult,
  CostTransferDetail,
  CostTransferItemRow,
  CostTransferQuery,
  CostTransferRow
} from './api/consumption';
export { createConsumptionApi } from './api/consumption';
export type {
  ApprovalSlaNodeRow,
  ApprovalSlaResult,
  DeliverySlaResult,
  DeliverySlaRow,
  SlaStatsQuery,
  StatsApi
} from './api/stats';
export { createStatsApi } from './api/stats';

export interface HqApiOptions extends HttpClientOptions {
  /** 主业务 API 根，需含 `/api` 段；默认同源 `/api`。 */
  baseUrl?: string;
  /** 资源服务根，默认同源 `/res`（Nginx 反代到 Hq.Res.Api 的 `/api/res`）。 */
  resBaseUrl?: string;
}

export interface HqApi {
  /** 主业务 API 客户端（Hq.Api）。 */
  http: HttpClient;
  /** 资源服务客户端（Hq.Res.Api）。 */
  res: HttpClient;
  apply: ApplyApi;
  approval: ApprovalApi;
  /** 审批流配置（流程/节点/路由/变量/委托）。 */
  approvalFlow: ApprovalFlowApi;
  delivery: DeliveryApi;
  /** 配送轨迹（时间轴 + 节点回传）。 */
  track: TrackApi;
  /** RFID / 智能柜读取流水（批量上报 + 查询）。 */
  rfid: RfidApi;
  /** 运营端接单中心（外仓供货指令）。 */
  orderCenter: OrderCenterApi;
  stock: StockApi;
  /** 库存/效期预警（列表 / 汇总 / 扫描 / 关闭）。 */
  stockAlert: StockAlertApi;
  /** 缺货商品在线申请（科室提交 / 运营处理）。 */
  shortage: ShortageApi;
  /** 自动补货建议（规则扫描 / 采纳转请购 / 忽略）。 */
  replenish: ReplenishApi;
  /** 库存预警/补货规则（安全库存、补货点、批量）。 */
  stockRule: StockRuleApi;
  material: MaterialApi;
  outbound: OutboundApi;
  inbound: InboundApi;
  /** 退换货（`return` 是保留字，故命名 returns）。 */
  returns: ReturnApi;
  billing: BillingApi;
  /** 基础资料：单位 / 品类 / 供应商 / 价格协议。 */
  catalog: CatalogApi;
  /** 请购与采购。 */
  procurement: ProcurementApi;
  /** 外仓供货指令（`GY` 单，入即出）。 */
  supply: SupplyApi;
  /** 物资调价申请（提前 30 天书面申请 + 审批）。 */
  priceChange: PriceChangeApi;
  /** 服务会话消息（实时售后回复 / 24h 响应举证）。 */
  message: MessageApi;
  /** 售后/维修工单（`WX` 单）。 */
  workorder: WorkorderApi;
  /** 满意度评价（配送/质量/售后/平台）。 */
  satisfaction: SatisfactionApi;
  /** 驾驶舱/监管统计。 */
  analytics: AnalyticsApi;
  /** 监管驾驶舱（五级穿透 / 排行 / 绩效 / 专题 / 报告 / 医共体视角）。 */
  gov: GovApi;
  /** 系统管理：用户 / 角色 / 权限。 */
  admin: AdminApi;
  /** 平台运营端：租户生命周期 / 平台总览 / 行政区划（SYSTEM 门户）。 */
  tenant: TenantApi;
  /** 平台基础数据：角色/审批流模板、指标定义、消息模板、平台参数（SYSTEM 门户）。 */
  platform: PlatformApi;
  /** App 版本管理：版本配置 + 安装包发布元数据登记（文件由站点本地存 apps）+ 端侧检查更新（SYSTEM 门户）。 */
  appVersion: AppVersionApi;
  /** 运营补充：数据字典 / 接口日志 / 波次拆单 / 询价比价 / 预算定额。 */
  opsExtras: OpsExtrasApi;
  /** 审计日志（跨租户举证）。 */
  audit: AuditApi;
  /** 机构主数据：医院 / 分院 / 医共体成员 / 社区医院。 */
  org: OrgApi;
  /** 医共体 / 集团（统招分签归口）。 */
  orgGroup: OrgGroupApi;
  /** 机构仓库与科室（新机构落地必需）。 */
  orgStructure: OrgStructureApi;
  /** 地图能力：地址→坐标、高德 Web 端配置、存量坐标回填。 */
  geo: GeoApi;
  /** 二级库消耗确认与成本转移（CB 单）。 */
  consumption: ConsumptionApi;
  /** 时效（SLA）达成率：审批节点 / 配送。 */
  stats: StatsApi;
  transfer: InventoryOpsApi['transfer'];
  stocktake: InventoryOpsApi['stocktake'];
  file: FileApi;
  auth: AuthApi;
  /** 扫码绑定三件套：生成二维码 / 核验 / 微信 openid 绑定与免登。 */
  bind: BindApi;
  /** 扫码码值解析（申领 SQ / 配送 PS / 盘点 PD / 物资编码 / 绑定码）。 */
  scan: ScanApi;
  /** 常购清单（一键复购）。 */
  favorite: FavoriteApi;
  /** AI 问答（规则引擎取真实数据 + 大模型可选润色）。 */
  ai: AiChatApi;
  /** AI 中台：需求预测 / 降本推荐 / 结论依据反查（DR-13.9）。 */
  aiInsights: AiInsightsApi;
  /** 微信订阅消息（待办催办 / 配送到达 / 库存预警 / 退换货结果）。 */
  wxSubscribe: WxSubscribeApi;
  /** 钉钉小程序免登与绑定（`dd.getAuthCode`）。 */
  dingtalk: DingTalkApi;
  /** 支付宝小程序免登与绑定（`my.getAuthCode` → `alipay.system.oauth.token`）。 */
  alipay: AlipayApi;
}

/** 三端统一入口：注入各端适配器与令牌来源即可复用全部接口。 */
export function createHqApi(options: HqApiOptions): HqApi {
  const { baseUrl, resBaseUrl, ...rest } = options;
  const http = createHttpClient({ ...rest, baseUrl: baseUrl ?? '/api' });
  // 资源服务的 401 只让本次上传失败，不触发主会话清理：
  // 它常因 Res:Auth:JwtKey 与主后端不一致而返回 401，踢出登录会让用户以为"登录态失效"
  const res = createHttpClient({ ...rest, baseUrl: resBaseUrl ?? '/res', onUnauthorized: undefined });
  const ops = createInventoryOpsApi(http);

  return {
    http,
    res,
    apply: createApplyApi(http),
    approval: createApprovalApi(http),
    approvalFlow: createApprovalFlowApi(http),
    delivery: createDeliveryApi(http),
    track: createTrackApi(http),
    rfid: createRfidApi(http),
    orderCenter: createOrderCenterApi(http),
    stock: createStockApi(http),
    stockAlert: createStockAlertApi(http),
    shortage: createShortageApi(http),
    replenish: createReplenishApi(http),
    stockRule: createStockRuleApi(http),
    material: createMaterialApi(http),
    outbound: createOutboundApi(http),
    inbound: createInboundApi(http),
    returns: createReturnApi(http),
    billing: createBillingApi(http),
    catalog: createCatalogApi(http),
    procurement: createProcurementApi(http),
    supply: createSupplyApi(http),
    priceChange: createPriceChangeApi(http),
    message: createMessageApi(http),
    workorder: createWorkorderApi(http),
    satisfaction: createSatisfactionApi(http),
    analytics: createAnalyticsApi(http),
    gov: createGovApi(http),
    admin: createAdminApi(http),
    tenant: createTenantApi(http),
    platform: createPlatformApi(http),
    appVersion: createAppVersionApi(http),
    opsExtras: createOpsExtrasApi(http),
    audit: createAuditApi(http),
    org: createOrgApi(http),
    orgGroup: createOrgGroupApi(http),
    orgStructure: createOrgStructureApi(http),
    geo: createGeoApi(http),
    consumption: createConsumptionApi(http),
    stats: createStatsApi(http),
    transfer: ops.transfer,
    stocktake: ops.stocktake,
    file: createFileApi(res),
    auth: createAuthApi(http),
    bind: createBindApi(http),
    scan: createScanApi(http),
    favorite: createFavoriteApi(http),
    ai: createAiChatApi(http),
    aiInsights: createAiInsightsApi(http),
    wxSubscribe: createWxSubscribeApi(http),
    dingtalk: createDingTalkApi(http),
    alipay: createAlipayApi(http)
  };
}
